import Vue from 'vue';
import { Store } from 'vuex';

import { hasUCConsentBanner } from '@/helpers/tracking';
import { removeDuplicateByType } from '@/helpers/tracking/util';
import { getInfluencedSessionsContexts } from '@/libraries/tracking/sessions';

import { ApplicationContextInput, ObjectType } from '@/@types/graphql-types';

import Provider from '@/interfaces/provider';
import { until } from '@vueuse/core';

import type { UrlMetadata } from '@/stores/routing.store';

import {
	LegacySnowplowPushContext,
	SnowplowCampaignContextV1,
	SnowplowCampaignContextV2,
	SnowplowContext,
	SnowplowDevelopmentTestContext,
	SnowplowFilterContext,
	SnowplowFreemiumContext,
	SnowplowLoginContext,
	SnowplowPackageContext,
	SnowplowPageTypeContext,
	SnowplowPushContext,
	SnowplowTitleContextGraphql,
	SnowplowAudienceContext,
	SnowplowRecommenderContext,
	SnowplowHashedLoggedInUserContext,
	SnowplowExperimentContext,
	SnowplowSessionEnvironmentContext,
	SnowplowInteractionContext,
	SnowplowLinkToContext,
	SnowplowLinkContext,
} from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingProvider, TrackingProviderPropertiesInterface } from '@/helpers/tracking/providers/tracking-provider';

import { TitleListName } from '@/interfaces/responses/title-list';
import { ReadyType } from '@/stores/ready.store';
import { Consent, CONSENT_PARTNERS, EMAIL_HASH_KEY, UserGetters } from '@/stores/user.store';

import { APP_COMMIT_HASH, APP_PLATFORM, APP_VERSION } from '@/stores/app.store';
import { getVm } from '@/helpers/vm-helper';
import { TitleContentType } from '@/interfaces/snowplow/title-context';
import { TitleDetailState } from '@/stores/title-details.store';
import { paToParentIDMap } from './util/paParentMapping';
import { Route } from 'vue-router';
import { useUserStats } from '@/helpers/composables/useUserStats';
import { OverridePageState, SessionEnvironment } from '@/stores/tracking.store';
import { getExperimentStatus } from '@/libraries/experiment/composables/useExperiment';

interface TrackingSession {
	context: SnowplowCampaignContextV1 | SnowplowCampaignContextV2 | LegacySnowplowPushContext | SnowplowPushContext;
	timestamp: number;
}

export type TrackingListEventPayload = { action: string; property?: string | 'similartitles' };

export interface GetTitleContextArguments {
	jwEntityId?: string;
	titleId: number;
	objectType: ObjectType;
	seasonNumber?: number;
	episodeNumber?: number;
	contentType?: TitleContentType | undefined;
}

/**
 * Simple wrapper around different event tracking services which provides
 * a streamlined interface for tracking events and page views.
 */
class TrackingHelperClass {
	private trackers: TrackingProvider[] = [];
	private store: Store<any>;
	private sessions: TrackingSession[] = [];
	private sessionDuration = 60 * 30 * 1000; // 30 minutes
	private notBounced = false;

	private isInitialized = false;
	private resolveInitializedPromise: (value: boolean) => void;
	private isFirstEventQueue = true;

	isUserOptedOut = false;
	isInitializedPromise = new Promise<boolean>(res => (this.resolveInitializedPromise = res));

	private setInitialized() {
		this.resolveInitializedPromise(true);
		this.isInitialized = true;
	}

	setUserOptedOut() {
		this.isUserOptedOut = true;
		this.store.dispatch('user/setOptedOutConsent', true);
	}

	/**
	 * interim solution to resets the global state of the tracking helper.
	 * in a perfect world we have the trackinghelper included/merged and reinitialised in the tracking store.
	 */
	resetState() {
		this.sessions = [];
		this.notBounced = false;
	}

	get isDisabled(): boolean {
		return !!process.env.VUE_APP_BOOK;
	}

	setStore(store: Store<any>) {
		this.store = store;
	}

	/**
	 * jwId has been resolved and will now be used to initialize all the trackers and process the queue.
	 */
	async initializeTrackers(trackers: TrackingProvider[], force: boolean = false) {
		const { store } = this;
		const { language } = store.state;
		const _hasUCConsentBanner = hasUCConsentBanner(language.webLocale, false);
		const isUserOptedOut = store.state.user['optedOutConsent'];
		if (isUserOptedOut) {
			return;
		}

		// await with loading tracking libs until any consent is accepted or rejected
		if (!global.VUE_APP_TESTS_E2E && _hasUCConsentBanner) {
			if (!force) await Vue.$jw.ready?.waitFor(ReadyType.CONSENT_PARTNERS_ANSWERED);
		}

		const initTrackerPromises = trackers
			.filter(
				tracker =>
					// either it's not in a GDPR country
					!_hasUCConsentBanner ||
					// or it is and its DPS has been consented to
					(_hasUCConsentBanner &&
						(tracker.dataProcessingService === null ||
							store.getters['user/dpsConsents']![tracker.dataProcessingService]))
			)
			.map(async (tracker: TrackingProvider): Promise<TrackingProvider | boolean> => {
				try {
					// Pass store to tracker if it has a setStore method (e.g., Snowplow)
					if ('setStore' in tracker && typeof tracker.setStore === 'function') {
						tracker.setStore(store);
					}
					await tracker.initialize();
					return tracker;
				} catch (err) {
					console.error('[Tracker runtime error: initialize', err);
					return true;
				}
			});

		this.trackers = (await Promise.all(initTrackerPromises)).filter(t => t !== true) as TrackingProvider[];
		this.setInitialized();

		// possibly add campaign contexts
		this.handleAddingCampaignContexts();

		this.processQueue();
	}

	async registerTracker(newTracker: TrackingProvider) {
		// check if tracker is already initialized
		const existingTracker = this.trackers.find(tracker => tracker.constructor === newTracker.constructor);
		if (existingTracker) return;

		await newTracker.initialize();
		this.trackers.push(newTracker);
	}

	async unRegisterTracker(tracker: TrackingProvider) {
		// filter out tracker to be unregistered
		this.trackers = this.trackers.filter(existingTracker => existingTracker.constructor !== tracker.constructor);
	}

	private trackEnrichedEvents(event: any): void {
		const enrichedEvent = this.enrichQueueEvent(event);
		const { category, properties, contexts } = enrichedEvent;
		this.trackers.forEach((tracker: TrackingProvider) => {
			try {
				tracker.trackEvent(category, properties, contexts);
			} catch (err) {
				console.error('[Tracker runtime error: events', err);
			}
		});
	}

	private async processQueue() {
		// MERGE NOTE: re enable this for snowplow upgrade as experiment
		// await Vue.$jw.ready?.waitFor(ReadyType.SNOWPLOW_INIT);

		const { store } = this;
		const isUserOptedOut = store.state.user['optedOutConsent'];
		if (this.isInitialized && !isUserOptedOut) {
			// most events are the start of more CPU intense tasks.
			// in order to not block main thread even more, we'll await until the most critical tasks are done (500ms drove better results than $nextTick).
			await new Promise(resolve => setTimeout(resolve, 500));

			store.getters['tracking/getQueueOf']('pageViews').forEach((pageViewEvent: any) => {
				const enrichedEvent = this.enrichQueueEvent(pageViewEvent);

				this.trackers.forEach((tracker: TrackingProvider) => {
					try {
						tracker.trackPage.apply(tracker, [enrichedEvent]);
					} catch (err) {
						console.error('[Tracker runtime error: pageViews]', err);
					}
				});
			});

			this.store.dispatch('tracking/resetQueue', 'pageViews');

			// events
			store.getters['tracking/getQueueOf']('events').forEach((event: any) => {
				this.trackEnrichedEvents(event);
			});

			this.store.dispatch('tracking/resetQueue', 'events');
			this.setFirstEventQueueComplete();
		}
	}

	// Some contexts are not fully populated first events are queued.
	// For now we only need to add session Environment context, but can add more here.
	enrichQueueEvent(queuedEvent: any) {
		if (
			!this.isFirstEventQueue ||
			// first pageview queue also has audience refresh pageview evt
			queuedEvent.contexts.some((ctx: SnowplowContext) => ctx.__name === 'SessionEnvironmentContext')
		) {
			return queuedEvent;
		}

		const sessionEnvironment = new SnowplowSessionEnvironmentContext(this.store.state.tracking.sessionEnvironment);

		return {
			...queuedEvent,
			contexts: [...queuedEvent.contexts, sessionEnvironment],
		};
	}

	setFirstEventQueueComplete() {
		if (this.isFirstEventQueue) {
			this.isFirstEventQueue = false;
		}
	}

	async trackPage(path: string, additionalContexts: SnowplowContext[] = []) {
		const { store } = this;
		const { routing, tracking, titleDetails } = this.store.state;

		const title = routing.urlMetadata?.metaTitle || 'Justwatch';
		const titleId = titleDetails.titleContextArguments?.jwEntityId;

		// wait for metaDataPromise
		await routing.urlMetadataPromise;

		const activeRoute = (routing.activeRoute || {}) as Route;

		// Refferal Module Context
		const isInteractivePageView = tracking.pagesVisited > 1;
		let contexts = await this.getContexts({
			contexts: [],
			pathname: path,
			nonInteraction: !isInteractivePageView,
		});

		if (additionalContexts.length) {
			contexts = contexts.concat(additionalContexts);
		}

		if (isInteractivePageView) {
			await this.checkNotBounced();
		}

		const isLoggedIn = store.getters['user/isLoggedIn'];

		// for now titledetailpage events are fired from the page component, so activeRoute should always be available.
		// Otherwise it may make sense to use metaContexts.includes('title')

		const pageHasPermanentAudiences =
			activeRoute.name?.includes('detail') || activeRoute.name?.includes('subgenre');
		if (pageHasPermanentAudiences && isLoggedIn) {
			const userContexts = this.getLoggedInUserContexts();
			contexts = contexts.concat(userContexts);
		}

		const permanent_audiences = store.getters['titleDetails/getPermanentAudiences'];

		if (permanent_audiences.length) {
			const audienceContext = await this.getAudienceContext(permanent_audiences);
			contexts = contexts.concat(audienceContext);
		}

		// add subgenre contexts separately
		const permanent_audiences_subgenres = store.getters['user/getPermanentAudiencesSubgenres'];

		if (permanent_audiences_subgenres.length && activeRoute.name?.includes('subgenre')) {
			const audienceContext = await this.getAudienceContext(permanent_audiences_subgenres);
			contexts = contexts.concat(audienceContext);
		}

		const pageViewEvent = { path, title, permanent_audiences, contexts, titleId };
		this.store.dispatch('tracking/addPageView', pageViewEvent);

		this.processQueue();
	}

	async trackAudienceRefresh(permanentAudiences: string[] = []) {
		await this.store.state.routing.urlMetadataPromise;

		const contexts: SnowplowContext[] = await this.getAudienceContext(permanentAudiences);

		this.trackEvent('audience', { action: 'refresh', nonInteraction: true }, contexts);
	}

	refreshPermanentAudiences(userPermanentAudiences: string[] = []) {
		// new audiences get added as a user visits pages
		const sessionPermanentAudiences: string[] = this.store.getters['titleDetails/getPermanentAudiences'];
		const permanentAudiences = new Set([...userPermanentAudiences, ...sessionPermanentAudiences]);
		this.trackers.forEach((tracker: TrackingProvider) => {
			try {
				tracker.refreshPermanentAudiences.apply(tracker, [[...permanentAudiences]]);
			} catch (err) {
				console.error('[Tracker runtime error: refreshPermanentAudiences', err);
			}
		});
	}

	refreshPermanentAudiencesSubgenres(userPermanentAudiences: string[]) {
		// new audiences get added as a user visits pages
		const sessionPermanentAudiences: string[] = this.store.getters['user/getPermanentAudiencesSubgenres'];
		const permanentAudiences = new Set([...userPermanentAudiences, ...sessionPermanentAudiences]);
		this.trackers.forEach((tracker: TrackingProvider) => {
			try {
				tracker.refreshPermanentAudiencesSubgenres.apply(tracker, [[...permanentAudiences]]);
			} catch (err) {
				console.error('[Tracker runtime error: refreshPermanentAudiencesSubgenres', err);
			}
		});
	}

	/**
	 * Tracks a structured event.
	 * NOTE: Use without await, as it doesn't make sense for user to wait on this.
	 * NOTE: You can use await along with the immediate parameter if you need to track an event before a critical process such as navigation or page unload so it completes before proceeding
	 * @TODO check if tracking is disabled in consents
	 */
	async trackEvent(
		category: string,
		properties: TrackingProviderPropertiesInterface,
		contexts: SnowplowContext[] = [],
		force: boolean = false,
		immediate: boolean = false
	) {
		if (this.isDisabled) {
			return;
		}

		const { routing } = this.store.state;

		// argument validation
		if (!category) {
			throw new Error('Expected "category" to be a non-empty string value');
		}

		// make sure the correct urlMetadata is loaded and is not fired too early, too
		if (!force) await routing.urlMetadataPromise;

		properties.path = routing.activeRoute?.fullPath;
		if (!properties.path) {
			return;
		}
		// interacting with the consent modal should not count towards our Boune measurements
		if (category === 'consent') properties.nonInteraction = true;

		contexts = await this.getContexts({
			contexts: contexts,
			pathname: properties.path,
			useLastRoute: properties.useLastRoute,
			nonInteraction: properties.nonInteraction,
		});

		const trackEvent = { category, properties, contexts };

		if (!properties.nonInteraction) {
			await this.checkNotBounced(immediate);
		}

		// If immediate flag is true, send the event directly to trackers without queuing
		// This is necessary for navigation/unload scenarios where the page might unload before queue processes
		if (immediate) {
			// Wait for trackers to be initialized if they aren't yet
			if (!this.isInitialized) {
				await this.isInitializedPromise;
			}

			if (!this.isUserOptedOut && this.trackers.length > 0) {
				this.trackEnrichedEvents(trackEvent);
			}
			return;
		}

		this.store.dispatch('tracking/addTrackEvent', trackEvent);

		// not bounced events are triggered by other events already,
		// so they don't need to additionally processQueue, it's done by the initiator already.
		if (category !== 'not_bounced') {
			this.processQueue();
		}

		// TODO: remove the if-statement as soon as the reason for this problem
		// snowplow tracking error is figured out
		// reference trello card 121-snowplow-tracking-erroneous-home-page-view-events
		// if (category === 'home_page_view' && properties.path !== '/') {
		// 	const currentEvent = {
		// 		category,
		// 		properties,
		// 	};
		// 	captureMessageForSentry('Home page view fired with wrong path', currentEvent);
		// 	console.log('Home page view fired with wrong path', properties.path, currentEvent);
		// }
	}

	async trackConsent(consents: Record<Consent, boolean>) {
		if (typeof window === 'undefined') {
			return Promise.resolve('Unknown');
		}

		(Object.keys(consents) as Consent[]).forEach(consentName => {
			const consentType = consents[consentName] ? 'trackConsentGranted' : 'trackConsentWithdrawn';
			const consentId = CONSENT_PARTNERS[consentName].id;
			const all = false;
			const version = '1';
			const name = null;
			const description = null;
			const date = new Date();
			date.setMonth(date.getMonth() + 50);
			const expiry = date.toISOString();

			if (consentType === 'trackConsentGranted') {
				window.snowplow(consentType, consentId, version, name, description, expiry, []);
			} else if (consentType === 'trackConsentWithdrawn') {
				window.snowplow(consentType, all, consentId, version, name, description, []);
			}
		});
	}

	async trackFilterToggleEvent(
		eventProperties: { label: string; property: any; value?: 0 | 1 },
		contexts: SnowplowContext[]
	) {
		const { label, property, value } = eventProperties;

		this.trackEvent(
			'userinteraction',
			{
				action: 'filter_toggled',
				label,
				property: property ?? 'all',
				value,
			},
			contexts
		);
	}

	async trackListEvent(
		listName: TitleListName,
		listAction: 'add' | 'remove',
		payload: TrackingListEventPayload,
		contexts: SnowplowContext[]
	) {
		this.trackEvent(`${listName}_${listAction}`, payload, contexts);
	}

	async getContexts(payload: {
		contexts: SnowplowContext[];
		pathname?: string;
		useLastRoute?: boolean;
		nonInteraction?: boolean;
	}): Promise<SnowplowContext[]> {
		const { contexts, pathname, useLastRoute = false, nonInteraction = false } = payload;
		// create an array of Snowplow context type (strings)
		const typeList = contexts.map(({ schema }) => schema);

		const baseContexts = this.getBaseContexts(pathname, useLastRoute);
		const filteredBaseContexts = baseContexts.filter(removeDuplicateByType(typeList));
		const additionalContexts = this.getAdditionalContexts(nonInteraction).filter(removeDuplicateByType(typeList));
		const influencedContexts = getInfluencedSessionsContexts().filter(removeDuplicateByType(typeList));

		return [...contexts, ...filteredBaseContexts, ...additionalContexts, ...influencedContexts];
	}

	getBaseContexts(pathname?: string, useLastRoute?: boolean): SnowplowContext[] {
		const { store } = this;
		const baseContexts: SnowplowContext[] = [];

		if (pathname) {
			const pageTypeContext: SnowplowPageTypeContext = this.getPageTypeContext(useLastRoute);

			baseContexts.push(pageTypeContext);
		}

		// TEST CONTEXT FOR DEV AND STAGING ENVIRONMENTS
		if (JW_CONFIG.DOMAIN === 'moviecycle.com') {
			const testIdentifier = 'development';
			const testContext = new SnowplowDevelopmentTestContext(true, testIdentifier);
			baseContexts.push(testContext);
		}

		// PROPERTY BASED
		// - login
		if (store.state.user.jwLoginId) {
			baseContexts.push(new SnowplowLoginContext(store.state.user.jwLoginId));
		}

		// SESSION ENVIRONMENT
		// context values are null on server, no need to add it here
		this.addSessionEnvironmentContext(baseContexts);

		return baseContexts;
	}

	addSessionEnvironmentContext(contexts: SnowplowContext[]) {
		// context values are null on server
		if (process.server) return;
		const sessionEnvironment: SessionEnvironment = this.store.state.tracking.sessionEnvironment;
		if (!sessionEnvironment.sessionReady) return;

		contexts.push(new SnowplowSessionEnvironmentContext(sessionEnvironment));
	}

	getLoggedInUserContexts(): SnowplowContext[] {
		if (process.server) return [];
		// get hashed user email on title pages
		const hash = JSON.parse(localStorage.getItem(EMAIL_HASH_KEY) || 'null');
		if (!hash) return [];

		return [new SnowplowHashedLoggedInUserContext(hash)];
	}

	getContextsWithForcedPageType(contexts: SnowplowContext[], pageType: string): SnowplowContext[] {
		const { store } = this;
		const baseContexts: SnowplowContext[] = [];
		// create an array of Snowplow context type (strings)
		const typeList = contexts.map(({ schema }) => schema);

		// PAGE CONTEXT
		if (pageType) {
			const pageTypeContext: SnowplowPageTypeContext = this.getForcedPageTypeContext(pageType);
			baseContexts.push(pageTypeContext);
		}

		// TEST CONTEXT FOR DEV AND STAGING ENVIRONMENTS
		if (JW_CONFIG.DOMAIN === 'moviecycle.com') {
			const testIdentifier = 'development';
			const testContext = new SnowplowDevelopmentTestContext(true, testIdentifier);
			baseContexts.push(testContext);
		}

		// PROPERTY BASED
		// - login
		if (store.state.user.jwLoginId) {
			baseContexts.push(new SnowplowLoginContext(store.state.user.jwLoginId));
		}

		// SESSION ENVIRONMENT
		if (process.client) {
			this.addSessionEnvironmentContext(baseContexts);
		}

		const filteredBaseContexts = baseContexts.filter(removeDuplicateByType(typeList));

		return [...contexts, ...filteredBaseContexts];
	}

	getAdditionalContexts(nonInteraction = false): SnowplowContext[] {
		const { store } = this;
		const additionalContexts: SnowplowContext[] = [];

		// ROUTE BASED
		// - filter
		const activeRoute = store.state.routing.activeRoute || {};
		const routeBasedContexts = (activeRoute.meta || {}).contexts || [];

		if (routeBasedContexts.includes('filter')) {
			const filters = store.getters['filter/currentFilters'];
			const providerShortNames = store.state.constant.providers.map((item: Provider) => item.shortName);
			const locale = store.getters['language/locale'];
			const urlMetadata = store.getters['routing/urlMetadata'];
			const pageId = urlMetadata.urlID;
			const searchQuery = this.store.getters['routing/queryParams'] || {
				q: this.store.getters['searchSuggester/query'],
			};

			const filterContext = new SnowplowFilterContext(filters, searchQuery, pageId, locale, providerShortNames);

			additionalContexts.push(filterContext);
		}

		// - title
		if (routeBasedContexts.includes('title') || !!store.state.tracking.overridePageState) {
			const titleDetails: TitleDetailState = store.state.titleDetails;
			if (titleDetails.titleContextArguments) {
				const { titleId, objectType, seasonNumber, episodeNumber, contentType } =
					titleDetails.titleContextArguments;

				additionalContexts.push(
					this.getTitleContextGraphql(titleId, objectType, seasonNumber, episodeNumber, contentType)
				);
			}
		}

		// SESSION BASED
		const now = Date.now();
		this.sessions
			.filter(session => now - this.sessionDuration < session.timestamp)
			.forEach(session => {
				// add session context
				additionalContexts.push(session.context);
				// refresh session timestamp
				session.timestamp = now;
			});

		// PRO SUBSCRIPTION BASED
		if (store.getters['user/isPremium']) {
			additionalContexts.push(this.getFreemiumContext());
		}

		// New Experiment Context
		const userExperimentsInfo = store.getters['user/userExperimentsInfo'] as UserGetters['userExperimentsInfo'];
		Object.entries(userExperimentsInfo).forEach(([name, experiment]) => {
			if (!!experiment.variant && experiment.activated) {
				const status = getExperimentStatus(name as any);
				additionalContexts.push(
					new SnowplowExperimentContext(name, experiment.affiliationShortId, '1', experiment.variant, status)
				);
			}
		});
		if (!nonInteraction) {
			const interactionContext = new SnowplowInteractionContext(!nonInteraction);
			additionalContexts.push(interactionContext);
		}

		return additionalContexts;
	}

	addSessionContext(context: TrackingSession['context']) {
		this.sessions.push({
			context,
			timestamp: Date.now(),
		});
	}

	getDomainSessionId(): Promise<string | null> {
		if (typeof window === 'undefined') {
			return Promise.resolve(null);
		}

		return new Promise(async resolve => {
			await Vue.$jw.ready?.waitFor(ReadyType.SNOWPLOW_INIT);

			window.snowplow(function () {
				// @ts-ignore Typescript is being annoying here
				const { cf } = this;
				const domainUserInfo = cf?.getDomainUserInfo();
				const domainSessionId = domainUserInfo?.[6] ?? null;
				resolve(domainSessionId);
			});
		});
	}

	/**
	 * Set the permanent audience, and their parent audiences
	 * into a Snowplow context array
	 *
	 * @param permanentAudiences
	 *
	 * @returns baseContexts
	 */
	async getAudienceContext(permanentAudiences: string[]) {
		const baseContexts: SnowplowContext[] = [];
		const prefix = 'pa:' as const;
		const parentAudienceUniqueListIds: string[] = [];

		const {
			loading,
			permanentAudiences: historicPermanentAudiences,
			parentPermanentAudiences: historicParentPermanentAudiences,
		} = useUserStats();

		await until(loading).toBe(false);

		permanentAudiences.forEach((audience: string) => {
			baseContexts.push(
				new SnowplowAudienceContext(
					'permanent_audience',
					audience,
					null,
					historicPermanentAudiences.value.includes(audience) ? 'old' : 'fresh'
				)
			);
			const audienceID = audience.split(prefix)?.[1];

			if (!audienceID) return;

			const parentAudiences = paToParentIDMap[audienceID];
			if (!parentAudiences) return;

			parentAudiences.forEach((parentAudience: { listId: string; numericalId: number }) => {
				if (!parentAudienceUniqueListIds.includes(parentAudience.listId)) {
					baseContexts.push(
						new SnowplowAudienceContext(
							'parent_permanent_audience',
							`${prefix}${parentAudience.listId}`, // Transform with the pa: prefix
							parentAudience.numericalId.toString(),

							historicParentPermanentAudiences.value.includes(`${prefix}${parentAudience.listId}`)
								? 'old'
								: 'fresh'
						)
					);
					parentAudienceUniqueListIds.push(parentAudience.listId);
				}
			});
		});

		return baseContexts;
	}

	getTitleContextGraphql(
		titleObjectId: number,
		titleObjectType: ObjectType,
		seasonNumber?: number,
		episodeNumber?: number,
		contentType?: TitleContentType | undefined,
		index?: number | undefined
	) {
		return new SnowplowTitleContextGraphql(
			titleObjectId,
			titleObjectType,
			seasonNumber ?? null,
			episodeNumber ?? null,
			contentType ?? null,
			index
		);
	}

	getFreemiumContext() {
		const isHideSeenDislikedEnabled: boolean = this.store.getters['user/isHideSeenDislikedTitlesEnabled'];

		return new SnowplowFreemiumContext(isHideSeenDislikedEnabled);
	}

	getPackageContext(
		packageId: number,
		packageName: string,
		parentType?: null | 'package' | 'bundle',
		parentId?: number,
		parentName?: string
	) {
		return new SnowplowPackageContext(packageId, packageName, parentType, parentId, parentName);
	}

	getRecommenderContext(
		source: string,
		source_title?: string | null,
		source_season_number?: number | null,
		influenced?: boolean | null,
		composition_id?: string | null,
		minScore?: number | null,
		maxScore?: number | null,
		avgScore?: number | null,
		rawScore?: number | null
	) {
		return new SnowplowRecommenderContext(
			source,
			source_title,
			source_season_number,
			influenced,
			composition_id,
			minScore,
			maxScore,
			avgScore,
			rawScore
		);
	}

	/** Correctly setup and return page type context for a given page */
	getPageTypeContext(useLastRoute = false) {
		const { store } = this;
		const language: string = store.getters['language/language'];
		const country: string = store.getters['language/country'];
		const pageViewUUID: string | null = store.state.tracking.pageViewUUID;

		const overridePageState: OverridePageState = store.state.tracking.overridePageState;

		const routeMetaData = useLastRoute ? store.state.routing.lastUrlMetadata : store.state.routing.urlMetadata;
		const metaData: UrlMetadata = routeMetaData;

		const pageType = getPageType(metaData);

		// Temporarily overrides pageType and pageViewUUID if when, for example, the title modal is opened
		if (!!overridePageState) {
			return new SnowplowPageTypeContext(
				overridePageState.pageType.toLowerCase(),
				country,
				language,
				overridePageState.pageViewUUID,
				overridePageState.pageEntryMode
			);
		}

		return new SnowplowPageTypeContext(pageType, country, language, pageViewUUID);
	}

	/**
	 * Overwrite pageType, for AudienceRefresh
	 *
	 *
	 * @returns {SnowplowPageTypeContext} context
	 */
	getForcedPageTypeContext(pageType: string): SnowplowPageTypeContext {
		const { store } = this;
		const language: string = store.getters['language/language'];
		const country: string = store.getters['language/country'];
		const pageViewUUID: string | null = store.state.tracking.pageViewUUID;

		return new SnowplowPageTypeContext(pageType, country, language, pageViewUUID);
	}

	/**
	 * check if not_bounced needs to be sent as well.
	 * @param immediate - If true, sends the not_bounced event immediately without queuing
	 */
	private async checkNotBounced(immediate: boolean = false) {
		if (!this.notBounced) {
			this.notBounced = true;
			// await this part, so we can still add not_bounced to the queue before we processQueue.
			await this.trackEvent(
				'not_bounced',
				{
					nonInteraction: true,
				},
				[],
				false,
				immediate
			);
		}
	}

	/**
	 * checks if campaign contexts should be added for this session.
	 */
	private handleAddingCampaignContexts() {
		if (typeof window === 'undefined') {
			return;
		}

		const uri = window.location.search.substring(1);
		const params = new URLSearchParams(uri);

		if (params.get('uct_b')) {
			// add campaign context v2
			const campaignContext = new SnowplowCampaignContextV2(
				params.get('uct_b')!,
				params.get('uct_s')!,
				params.get('uct_i')!,
				params.get('uct_at')!,
				params.get('uct_tn')!,
				`${window.location.protocol}//${window.location.host}${window.location.pathname}`,
				params.get('uct_p'),
				params.get('uct_sid') ? parseInt(params.get('uct_sid')!) : null,
				params.get('uct_cid') ? parseInt(params.get('uct_cid')!) : null,
				params.get('uct_adgid') ? parseInt(params.get('uct_adgid')!) : null,
				params.get('uct_crid') ? parseInt(params.get('uct_crid')!) : null,
				params.get('uct_adid') ? parseInt(params.get('uct_adid')!) : null,
				params.get('uct_ssid') ? parseInt(params.get('uct_ssid')!) : null,
				params.get('uct_scid') ? parseInt(params.get('uct_scid')!) : null,
				params.get('uct_eccid') ? parseInt(params.get('uct_eccid')!) : null
			);
			this.addSessionContext(campaignContext);
		}
	}
}

// return singleton of trackinghelper
export const TrackingHelper = new TrackingHelperClass();

export let TrackingAppId = `${APP_VERSION}-${APP_PLATFORM}#${APP_COMMIT_HASH}`;
if (global.IS_CANARY) {
	console.debug('[CANARY RELEASE]');
	TrackingAppId += `:canary`;
}

export const ApplicationContext: ApplicationContextInput = {
	appID: TrackingAppId,
	platform: APP_PLATFORM,
	version: APP_VERSION,
	build: APP_COMMIT_HASH,
	isTestBuild: JW_CONFIG.DOMAIN !== 'justwatch.com' || process.env.NODE_ENV === 'development',
	// OPTIONAL: extras: json string of k:v pairs for extra information
};

// NOTE: there is some redundancy here with page type and HOME routes
// `routing.store:fetchUrlMetadata` sets a mock value for '/' (HOME) but
// then also calls this function which itself reads the set urlMetadata
// I've updated the routing store to set the proper page type for home with the onboarding status
export function getPageType(urlMetadata: UrlMetadata | null) {
	const { $route } = getVm() ?? {};
	const { pageType } = $route?.meta ?? {};

	// for pages where the technicalName is different to the pageType.
	// the $route isnt set when the first pageView event is fired, so we need to set this using the technical name
	const pageTypeMapping = {
		VIEW_SUBGENRE_MOVIE: 'VIEW_POPULAR_SUBGENRE_MOVIES',
		VIEW_SUBGENRE_SHOWS: 'VIEW_POPULAR_SUBGENRE_SHOWS',
		VIEW_ARTICLE: 'VIEW_ARTICLE',
	} as const;

	type pageTypeMappingKeys = keyof typeof pageTypeMapping;

	const pageTypeNew = Object.keys(pageTypeMapping).find(key =>
		(urlMetadata?.page?.technicalName || '').startsWith(key)
	);

	if (pageTypeNew) return pageTypeMapping[pageTypeNew as pageTypeMappingKeys];

	// For home pages this value gets set with some "mock" data in the routing.store
	if (urlMetadata?.page?.technicalName) return urlMetadata.page.technicalName;

	if (urlMetadata?.objectType) return urlMetadata.objectType;

	return `VIEW_${pageType}`;
}

/**
 * Transform LinkContext to LinkToContext used for linking the follow-up events to the clickout source.
 */
export function transformContextToLinkToContext(context: SnowplowContext) {
	if (context?.__name === 'LinkContext') {
		const LinkContext = context as SnowplowLinkContext;
		return new SnowplowLinkToContext({
			clickout: LinkContext.link.self,
			impression: LinkContext.link.impression,
		});
	}
	return context;
}
